import { InputAdornment, TextField } from "@mui/material";
import { Col, Row, Form } from "react-bootstrap";
import RefreshIcon from "@mui/icons-material/Refresh";
import Button from "@mui/material/Button";
import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { useLoader } from "../../context/LoaderContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getAvailableMetadataDefinition,
  getEnabledMetadataDefinition,
  metadataDefinitionInsert,
} from "../../api/metadataDefinitionApi/metadataDefinitionApi";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { useSnackbar } from "../Common/SnackbarContext";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import LocalStorageUtil from "../../utils/localStorage";

function not(a: readonly number[], b: readonly number[]) {
  return a.filter((value) => !b.includes(value));
}

function intersectionLeft(a: readonly number[], b: any) {
  return b.filter((column: any) =>
    a.includes(column.mappingAutoId || column.metaDataFieldId)
  );
}

function intersectionRight(a: readonly number[], b: any) {
  return b.filter((column: any) => a.includes(column.mappingAutoId));
}

interface MetadataDefinitionActionProps {
  businessUnitData: any;
}

const MetadataDefinitionAction: React.FC<MetadataDefinitionActionProps> = ({
  businessUnitData,
}) => {
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [searchTermLeft, setSearchTermLeft] = useState("");
  const [searchTermRight, setSearchTermRight] = useState("");
  const location = useLocation();
  const action = location.state?.action || {};
  const rowData = location.state?.rowData || {};
  const { showSnackbar } = useSnackbar();
  const [history, setHistory] = useState<
    Array<{
      left: any[];
      right: any[];
      checked: readonly number[];
    }>
  >([]);
  const [data] = useState<any>([]);
  const [checked, setChecked] = React.useState<readonly number[]>([]);
  const [left, setLeft] = useState<any[]>([]);
  const [right, setRight] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    metadataDefinitionName: rowData?.metadataDefinitionName || "",
  });
  const [errors, setErrors] = useState<{
    sourceNameError?: string;
    selectedFieldsError?: string;
  }>({});
  const leftChecked = intersectionLeft(checked, left);
  const rightChecked = intersectionRight(checked, right);
  const { setLoading } = useLoader();
  let foundData: any;

  const saveToHistory = () => {
    setHistory((prev) => {
      const newHistory = [...prev, { left, right, checked }];
      return newHistory;
    });
  };

  const undoLastAction = () => {
    if (history.length > 0) {
      const lastState = history[history.length - 1];
      setLeft([...lastState.left]);
      setRight([...lastState.right]);
      setChecked([...lastState.checked]);
      setHistory((prev) => prev.slice(0, -1));
    }
  };

  const handleConfirmDialogClose = (confirm: boolean) => {
    try {
      setConfirmDialogOpen(false);
      if (confirm) {
        navigate("/metadatadefinitiondisplay");
      } else {
        setConfirmDialogOpen(false);
      }
    } catch (error) {
      console.error("Error in handleConfirmDialogClose", error);
    }
  };

  const handelCancel = () => {
    setConfirmDialogOpen(true);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTermLeft(event.target.value);
  };

  const handleSearchChangeRight = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchTermRight(event.target.value);
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const getEnabledMetadataDefinitions = async () => {
    setLoading(true);
    const payloadforColumnHeaders = {
      AccountId:
        businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
      MetadataDefinitionId: rowData?.metadataDefinitionId,
      MetadataDefinitionName: rowData?.metadataDefinitionName,
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getEnabledMetadataDefinition(payloadforColumnHeaders);
      setRight(data.metaDataDefinitiongrid);
      return data;
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const getAvailableMetadataDefinitions = async (right: any) => {
    setLoading(true);
    const payloadforColumnHeaders = {
      AccountId:
        businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
      BusinessUnitId:
        businessUnitData?.businessUnitId ||
        LocalStorageUtil.getItem("businessUnitId"),
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getAvailableMetadataDefinition(
        payloadforColumnHeaders
      );
      if (Array.isArray(data)) {
        const allGroups = data.flatMap((item) => item.group || []);
        const separateObjects = data.filter((item) => item.field);
        const combinedData = [...allGroups, ...separateObjects];
        if (action !== "Edit") {
          setLeft(combinedData);
        }
        const filteredData = combinedData
          .filter(
            (item) => item.mappingAutoId !== 0 || item.iscustomizable === true
          )
          .filter(
            (item) =>
              !right.some(
                (rightItem: any) =>
                  item.mappingAutoId === rightItem.mappingAutoId ||
                  (item.mappingAutoId === 0 &&
                    item.iscustomizable === true &&
                    item.field === rightItem.field)
              )
          );
        setLeft(filteredData);
      } else {
        setLeft([]);
      }
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setFormData({
        metadataDefinitionName: data[0]?.metadataDefinitionName || "",
      });
    }
  }, [data]);
  useEffect(() => {
    (async () => {
      let data = [] as any;
      if (action === "Edit") {
        data = await getEnabledMetadataDefinitions();
      }
      getAvailableMetadataDefinitions(data?.metaDataDefinitiongrid);
    })();
  }, [businessUnitData]);

  function getMetaDataFieldId(mappingAutoId: number, iscustomizable: boolean) {
    foundData = right.find(
      (item) =>
        item.mappingAutoId === mappingAutoId &&
        item.iscustomizable === iscustomizable
    );
    return foundData ? foundData.metaDataFieldId : undefined;
  }

  const buildPayload = (dataList: any[], formData: any) => {
    return {
      MetaDataDefinition: dataList.map((item: any, index: number) => ({
        OrderBy: index + 1,
        MappingId:
          item.mappingAutoId === 0 ? item.metaDataFieldId : item.mappingAutoId,
        aliasName: item.aliasName || "",
        metaDataFieldId: getMetaDataFieldId(
          item.mappingAutoId,
          item.iscustomizable
        ),
        AccountId:
          businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
        isEnabled: true,
        category: foundData.category,
        field: foundData.category + " - " + foundData.field,
        MetadataDefinitionName: formData.metadataDefinitionName,
        CreatedBy: LocalStorageUtil.getItem("userId"),
        UpdatedBy: LocalStorageUtil.getItem("userId"),
        iscustomizable: item.mappingAutoId === 0,
        MetadataDefinitionId: rowData?.metadataDefinitionId,
      })),
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
    };
  };

  const processPayload = (
    existingData: any[],
    rightChecked: any[],
    leftChecked: any[],
    formData: any
  ) => {
    if (rightChecked.length > 0) {
      const newItems = rightChecked.filter(
        (item) =>
          !existingData.some((existing) => existing.field === item.field)
      );
      const updatedList = [...existingData, ...newItems];
      return buildPayload(updatedList, formData);
    } else if (leftChecked.length > 0) {
      const updatedList = existingData.filter(
        (item) => !leftChecked.some((checked) => checked.field === item.field)
      );
      return buildPayload(updatedList, formData);
    }
    return buildPayload(existingData, formData);
  };
  const handleCreate = async () => {
    setLoading(true);
    try {
      const newErrors: {
        sourceNameError?: string;
        selectedFieldsError?: string;
      } = {};
      if (!formData.metadataDefinitionName) {
        newErrors.sourceNameError = "Source Name is required";
      }
      if (right.length === 0) {
        newErrors.selectedFieldsError = "Please select atleast one field";
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
      } else {
        setErrors({});
        const length = right.length;
        const newValues = checked.map((val) => val);
        let MetadataItemValuesId = new Array(length).fill(0);
        const InactiveIds = checked.map((item) => item);
        MetadataItemValuesId = MetadataItemValuesId.map((id) =>
          InactiveIds.includes(id) ? 0 : id
        );
        const uniqueNewValues = Array.from(new Set(newValues));
        MetadataItemValuesId = MetadataItemValuesId.filter((id) => id !== 0);
        MetadataItemValuesId.push(...uniqueNewValues);
        const payload = processPayload(
          right,
          rightChecked,
          leftChecked,
          formData
        );
        const response = await metadataDefinitionInsert(payload);
        if (response) {
          navigate("/metadatadefinitiondisplay");
          showSnackbar("Metadata Definition Updated Successfully");
        } else {
          showSnackbar("Failed to update Metadata Definition");
        }
      }
    } catch (error) {
      console.error("Error creating metadata group:", error);
    } finally {
      setLoading(false);
    }
  };
  // Handle drag end
  const handleDragEnd = (result: any) => {
    const { source, destination } = result;
    if (!destination) return;

    saveToHistory();

    // Helper to get filtered lists
    const filteredLeft = left.filter(
      (item: any) =>
        searchTermLeft === "" ||
        item.field?.toLowerCase().includes(searchTermLeft.toLowerCase())
    );
    const filteredRight = right.filter(
      (item: any) =>
        searchTermRight === "" ||
        item.field?.toLowerCase().includes(searchTermRight.toLowerCase())
    );

    // Drag within the same list (reorder)
    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === "available") {
        // Map filtered index to actual index in left
        const actualSourceIndex = left.findIndex(
          (item) => item === filteredLeft[source.index]
        );
        const actualDestIndex = left.findIndex(
          (item) => item === filteredLeft[destination.index]
        );
        const newLeft = Array.from(left);
        const [moved] = newLeft.splice(actualSourceIndex, 1);
        newLeft.splice(actualDestIndex, 0, moved);
        setLeft(newLeft);
      } else if (source.droppableId === "enabled") {
        const actualSourceIndex = right.findIndex(
          (item) => item === filteredRight[source.index]
        );
        const actualDestIndex = right.findIndex(
          (item) => item === filteredRight[destination.index]
        );
        const newRight = Array.from(right);
        const [moved] = newRight.splice(actualSourceIndex, 1);
        newRight.splice(actualDestIndex, 0, moved);
        setRight(newRight);
      }
      return;
    }

    // Moving between lists
    if (
      source.droppableId === "available" &&
      destination.droppableId === "enabled"
    ) {
      // Map filtered index to actual index in left
      const actualSourceIndex = left.findIndex(
        (item) => item === filteredLeft[source.index]
      );
      const movedItem = left[actualSourceIndex];
      const newLeft = [...left];
      newLeft.splice(actualSourceIndex, 1);
      const newRight = [...right];
      newRight.splice(destination.index, 0, movedItem);
      setLeft(newLeft);
      setRight(newRight);
    } else if (
      source.droppableId === "enabled" &&
      destination.droppableId === "available"
    ) {
      const actualSourceIndex = right.findIndex(
        (item) => item === filteredRight[source.index]
      );
      const movedItem = right[actualSourceIndex];
      const newRight = [...right];
      newRight.splice(actualSourceIndex, 1);
      const newLeft = [...left];
      newLeft.splice(destination.index, 0, movedItem);
      setRight(newRight);
      setLeft(newLeft);
    }
  };

  return (
    <>
      {action === "Edit" ? (
        <NavigationBar
          menuTitle="Configuration"
          title="Metadata Management"
          screenTitle="Metadata Definition"
          text="Metadata Definition - Update Metadata Definition"
        />
      ) : (
        <NavigationBar
          menuTitle="Configuration"
          title="Metadata Management"
          screenTitle="Metadata Definition"
          text="Metadata Definition - Create Metadata Definition"
        />
      )}
      <div className="hideverticalbar">
        <Form>
          <Row className="mb-3" style={{ margin: "10px" }}>
            <Col md={3}>
              <Form.Group controlId="formGroupName">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Description<span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Source Name"
                  name="metadataDefinitionName"
                  value={formData.metadataDefinitionName}
                  isInvalid={!!errors.sourceNameError}
                  onChange={handleChange}
                  disabled={action === "Edit"}
                  required
                  style={{ fontSize: "11px" }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.sourceNameError}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Row style={{ margin: "10px" }}>
              <Col md={4}>
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Available<span className="required">*</span>
                </Form.Label>
                <TextField
                  fullWidth
                  placeholder="Search"
                  value={searchTermLeft}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    bgcolor: "white",
                    marginBottom: "10px",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root": {
                      height: "30px",
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: "#ccc",
                      },
                      "&:hover fieldset": {
                        borderColor: "#888",
                      },
                      "& input": {
                        fontSize: "11px",
                      },
                    },
                  }}
                  variant="outlined"
                />
                <Droppable droppableId="available">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        width: "100%",
                        height: "490px",
                        overflow: "auto",
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        fontSize: "11px",
                      }}
                    >
                      {left
                        .filter(
                          (item: any) =>
                            searchTermLeft === "" || // If searchTermLeft is empty, include all items
                            item.field
                              ?.toLowerCase()
                              .includes(searchTermLeft.toLowerCase())
                        )
                        .map((item: any, index: number) => (
                          <Draggable
                            key={item.mappingAutoId || item.metaDataFieldId}
                            draggableId={`${
                              item.mappingAutoId || item.metaDataFieldId
                            }`}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  padding: "8px",
                                  margin: "4px",
                                  backgroundColor: "white",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  cursor: "grab",
                                  color: "black",
                                }}
                              >
                                {`${String(index + 1).padStart(2, "0")}. ${
                                  item.category
                                    ? `${item.category} - ${item.field}`
                                    : item.field
                                }`}
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </Col>
              <Col
                md={1}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Button
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={undoLastAction}
                  disabled={history.length === 0}
                  aria-label="undo last action"
                  style={{
                    color: "var(--aggrid-text-color)",
                    border: "1px solid var(--aggrid-text-color)",
                  }}
                >
                  <RefreshIcon
                    fontSize="small"
                    sx={{
                      fontSize: "14px",
                      height: "14px",
                      width: "14px",
                    }}
                  />
                </Button>
              </Col>
              <Col md={4}>
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Enabled<span className="required">*</span>
                </Form.Label>
                <TextField
                  fullWidth
                  placeholder="Search"
                  value={searchTermRight}
                  onChange={handleSearchChangeRight}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    bgcolor: "white",
                    borderRadius: "8px",
                    marginBottom: "10px",
                    "& .MuiOutlinedInput-root": {
                      height: "30px",
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: "#ccc",
                      },
                      "&:hover fieldset": {
                        borderColor: "#888",
                      },
                      "& input": {
                        fontSize: "11px",
                      },
                    },
                  }}
                  variant="outlined"
                />
                <Droppable droppableId="enabled">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        width: "100%",
                        height: "490px",
                        overflow: "auto",
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        fontSize: "11px",
                      }}
                    >
                      {right
                        .filter(
                          (item: any) =>
                            searchTermRight === "" ||
                            item.field
                              ?.toLowerCase()
                              .includes(searchTermRight.toLowerCase())
                        )
                        .map((item: any, index: number) => (
                          <Draggable
                            key={item.mappingAutoId || item.metaDataFieldId}
                            draggableId={`${
                              item.mappingAutoId || item.metaDataFieldId
                            }`}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  padding: "8px",
                                  margin: "4px",
                                  backgroundColor: "white",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  cursor: "grab",
                                  color: "black",
                                }}
                              >
                                {`${String(index + 1).padStart(2, "0")}. ${
                                  item.category
                                    ? `${item.category} - ${item.field}`
                                    : item.field
                                }`}
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
                {errors.selectedFieldsError && (
                  <p style={{ color: "red" }}>{errors.selectedFieldsError}</p>
                )}
              </Col>
            </Row>
            <Row>
              <Col
                md={12}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "10px",
                  marginBottom: "10px",
                }}
              >
                <Button
                  variant="contained"
                  className="update-button"
                  onClick={handleCreate}
                >
                  {action === "Edit" ? "Update" : "Create"}
                </Button>
                <Button
                  variant="contained"
                  className="cancel-button"
                  onClick={handelCancel}
                  style={{ marginRight: "10px" }}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </DragDropContext>
        </Form>
      </div>
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        title={`Metadata Definition  - ${
          action === "Edit"
            ? "Update Metadata Definition"
            : "Create Metadata Definition"
        }`}
        content="Are you sure you want to Cancel?"
      />
    </>
  );
};

export default MetadataDefinitionAction;
